const DEFAULT_DOMAIN_MIN = [0, 0, 0];
const DEFAULT_DOMAIN_MAX = [1, 1, 1];

export class LutParseError extends Error {
  constructor(message) {
    super(message);
    this.name = 'LutParseError';
  }
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function getMeaningfulLines(source) {
  if (typeof source !== 'string') {
    throw new LutParseError('LUT source must be text.');
  }

  return source
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/u)
    .map((line) => line.replace(/#.*$/u, '').trim())
    .filter(Boolean);
}

function parseNumbers(tokens, context) {
  const values = tokens.map(Number);
  if (values.some((value) => !Number.isFinite(value))) {
    throw new LutParseError(`Invalid numeric value in ${context}.`);
  }
  return values;
}

function validateDomain(domainMin, domainMax) {
  for (let channel = 0; channel < 3; channel += 1) {
    if (domainMax[channel] <= domainMin[channel]) {
      throw new LutParseError('LUT domain maximum must exceed its minimum.');
    }
  }
}

function createNormalizedLut({
  format,
  size,
  domainMin = DEFAULT_DOMAIN_MIN,
  domainMax = DEFAULT_DOMAIN_MAX,
  shaper = null,
  values,
}) {
  if (!Number.isInteger(size) || size < 2) {
    throw new LutParseError('LUT cube size must be an integer of at least 2.');
  }

  const expectedValueCount = size ** 3 * 3;
  if (values.length !== expectedValueCount) {
    throw new LutParseError(
      `Expected ${size ** 3} LUT entries, received ${values.length / 3}.`
    );
  }

  validateDomain(domainMin, domainMax);

  if (shaper) {
    if (shaper.length !== size) {
      throw new LutParseError('The .3dl shaper length must match cube size.');
    }
    for (let index = 1; index < shaper.length; index += 1) {
      if (shaper[index] <= shaper[index - 1]) {
        throw new LutParseError('.3dl shaper values must be increasing.');
      }
    }
  }

  return {
    format,
    size,
    domainMin: [...domainMin],
    domainMax: [...domainMax],
    shaper: shaper ? Float32Array.from(shaper) : null,
    data: Float32Array.from(values),
  };
}

function normalizeCubeValues(values, size) {
  if (values.length !== size ** 3 * 3) {
    return values;
  }

  const normalized = new Array(values.length);
  // .cube files change red fastest; the sampler stores blue fastest.
  for (let blue = 0; blue < size; blue += 1) {
    for (let green = 0; green < size; green += 1) {
      for (let red = 0; red < size; red += 1) {
        const sourceIndex = (blue * size + green) * size + red;
        const targetIndex = (red * size + green) * size + blue;
        for (let channel = 0; channel < 3; channel += 1) {
          normalized[targetIndex * 3 + channel] =
            values[sourceIndex * 3 + channel];
        }
      }
    }
  }
  return normalized;
}

export function parseCubeLut(source) {
  const lines = getMeaningfulLines(source);
  let size = null;
  let domainMin = [...DEFAULT_DOMAIN_MIN];
  let domainMax = [...DEFAULT_DOMAIN_MAX];
  const values = [];

  lines.forEach((line) => {
    const tokens = line.split(/\s+/u);
    const directive = tokens[0].toUpperCase();

    if (directive === 'TITLE') {
      return;
    }
    if (directive === 'LUT_1D_SIZE') {
      throw new LutParseError('1D .cube LUTs are not supported.');
    }
    if (directive === 'LUT_3D_SIZE') {
      const parsedSize = Number(tokens[1]);
      if (!Number.isInteger(parsedSize)) {
        throw new LutParseError('Invalid LUT_3D_SIZE value.');
      }
      size = parsedSize;
      return;
    }
    if (directive === 'DOMAIN_MIN' || directive === 'DOMAIN_MAX') {
      const domain = parseNumbers(tokens.slice(1), directive);
      if (domain.length !== 3) {
        throw new LutParseError(`${directive} requires three values.`);
      }
      if (directive === 'DOMAIN_MIN') {
        domainMin = domain;
      } else {
        domainMax = domain;
      }
      return;
    }
    if (directive === 'LUT_3D_INPUT_RANGE') {
      const range = parseNumbers(tokens.slice(1), directive);
      if (range.length !== 2) {
        throw new LutParseError(`${directive} requires two values.`);
      }
      domainMin = [range[0], range[0], range[0]];
      domainMax = [range[1], range[1], range[1]];
      return;
    }

    const row = parseNumbers(tokens, '.cube data row');
    if (row.length !== 3) {
      throw new LutParseError('Each .cube data row requires three values.');
    }
    values.push(...row);
  });

  if (size === null) {
    throw new LutParseError('Missing LUT_3D_SIZE directive.');
  }

  return createNormalizedLut({
    format: 'cube',
    size,
    domainMin,
    domainMax,
    values: normalizeCubeValues(values, size),
  });
}

function inferIntegerScale(maximumValue) {
  if (maximumValue <= 1) {
    return 1;
  }

  const bitDepth = Math.ceil(Math.log2(maximumValue + 1));
  if (bitDepth > 24) {
    throw new LutParseError('.3dl output values exceed supported precision.');
  }
  return 2 ** bitDepth - 1;
}

export function parse3dlLut(source) {
  const lines = getMeaningfulLines(source);
  let outputBitDepth = null;
  const numericRows = [];

  lines.forEach((line) => {
    const meshMatch = /^mesh\s+\d+\s+(\d+)$/iu.exec(line);
    if (meshMatch) {
      outputBitDepth = Number(meshMatch[1]);
      return;
    }
    if (/^3dmesh$/iu.test(line)) {
      return;
    }

    const tokens = line.split(/\s+/u);
    const numeric = tokens.every((token) => Number.isFinite(Number(token)));
    if (!numeric) {
      throw new LutParseError(`Unsupported .3dl directive: ${tokens[0]}.`);
    }

    const values = parseNumbers(tokens, '.3dl row');
    numericRows.push(values);
  });

  const possibleShaper = numericRows[0];
  const hasShaper =
    possibleShaper?.length >= 2 &&
    possibleShaper.length ** 3 === numericRows.length - 1;
  const shaper = hasShaper ? possibleShaper : null;
  const rows = hasShaper ? numericRows.slice(1) : numericRows;

  if (rows.some((row) => row.length !== 3)) {
    throw new LutParseError('Each .3dl cube row requires three values.');
  }

  const inferredSize = Math.round(Math.cbrt(rows.length));
  const size = shaper?.length ?? inferredSize;
  if (size ** 3 !== rows.length) {
    throw new LutParseError('The .3dl row count does not form a cubic LUT.');
  }

  if (outputBitDepth !== null && outputBitDepth > 24) {
    throw new LutParseError(
      '.3dl output bit depth exceeds supported precision.'
    );
  }

  const maximumValue = rows.reduce(
    (maximum, row) => Math.max(maximum, ...row),
    0
  );
  const outputScale =
    outputBitDepth === null
      ? inferIntegerScale(maximumValue)
      : 2 ** outputBitDepth - 1;

  if (maximumValue > outputScale) {
    throw new LutParseError(
      '.3dl value exceeds the declared output bit depth.'
    );
  }

  const values = [];
  rows.forEach((row) => {
    row.forEach((value) => values.push(value / outputScale));
  });

  return createNormalizedLut({
    format: '3dl',
    size,
    shaper,
    values,
  });
}

export function parseLut(source, format) {
  const normalizedFormat = format?.replace(/^\./u, '').toLowerCase();
  if (normalizedFormat === 'cube') {
    return parseCubeLut(source);
  }
  if (normalizedFormat === '3dl') {
    return parse3dlLut(source);
  }

  const lines = getMeaningfulLines(source);
  const isCube = lines.some((line) => /^LUT_3D_SIZE\b/iu.test(line));
  return isCube ? parseCubeLut(source) : parse3dlLut(source);
}

function findShapedCoordinate(value, shaper) {
  const first = shaper[0];
  const last = shaper[shaper.length - 1];
  const target = first + clamp(value, 0, 1) * (last - first);

  if (target <= first) {
    return 0;
  }
  if (target >= last) {
    return shaper.length - 1;
  }

  let low = 0;
  let high = shaper.length - 1;
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (shaper[middle] <= target) {
      low = middle;
    } else {
      high = middle;
    }
  }

  const interval = shaper[high] - shaper[low];
  return low + (target - shaper[low]) / interval;
}

function getCoordinate(value, channel, lut) {
  const normalized = clamp(
    (value - lut.domainMin[channel]) /
      (lut.domainMax[channel] - lut.domainMin[channel]),
    0,
    1
  );
  return lut.shaper
    ? findShapedCoordinate(normalized, lut.shaper)
    : normalized * (lut.size - 1);
}

function getCubeIndex(size, red, green, blue) {
  return (red * size + green) * size + blue;
}

function getValue(lut, cubeIndex, channel) {
  return lut.data[cubeIndex * 3 + channel];
}

function interpolateChannel(lut, coordinates, channel) {
  const [red, green, blue] = coordinates;
  const r0 = Math.floor(red);
  const g0 = Math.floor(green);
  const b0 = Math.floor(blue);
  const r1 = Math.min(r0 + 1, lut.size - 1);
  const g1 = Math.min(g0 + 1, lut.size - 1);
  const b1 = Math.min(b0 + 1, lut.size - 1);
  const rt = red - r0;
  const gt = green - g0;
  const bt = blue - b0;

  const c000 = getValue(lut, getCubeIndex(lut.size, r0, g0, b0), channel);
  const c001 = getValue(lut, getCubeIndex(lut.size, r0, g0, b1), channel);
  const c010 = getValue(lut, getCubeIndex(lut.size, r0, g1, b0), channel);
  const c011 = getValue(lut, getCubeIndex(lut.size, r0, g1, b1), channel);
  const c100 = getValue(lut, getCubeIndex(lut.size, r1, g0, b0), channel);
  const c101 = getValue(lut, getCubeIndex(lut.size, r1, g0, b1), channel);
  const c110 = getValue(lut, getCubeIndex(lut.size, r1, g1, b0), channel);
  const c111 = getValue(lut, getCubeIndex(lut.size, r1, g1, b1), channel);

  const c00 = c000 + (c001 - c000) * bt;
  const c01 = c010 + (c011 - c010) * bt;
  const c10 = c100 + (c101 - c100) * bt;
  const c11 = c110 + (c111 - c110) * bt;
  const c0 = c00 + (c01 - c00) * gt;
  const c1 = c10 + (c11 - c10) * gt;
  return c0 + (c1 - c0) * rt;
}

function sampleLutInto(lut, coordinates, target) {
  coordinates[0] = getCoordinate(coordinates[0], 0, lut);
  coordinates[1] = getCoordinate(coordinates[1], 1, lut);
  coordinates[2] = getCoordinate(coordinates[2], 2, lut);

  for (let channel = 0; channel < 3; channel += 1) {
    target[channel] = interpolateChannel(lut, coordinates, channel);
  }
  return target;
}

export function sampleLut(lut, red, green, blue) {
  return sampleLutInto(lut, [red, green, blue], [0, 0, 0]);
}

export function applyLutToPixelData(pixelData, lut, intensity = 100) {
  if (!pixelData || pixelData.length % 4 !== 0) {
    throw new TypeError('RGBA pixel data length must be divisible by four.');
  }

  const output = new Uint8ClampedArray(pixelData);
  const mix = clamp(Number(intensity) || 0, 0, 100) / 100;
  if (mix === 0) {
    return output;
  }

  const sampled = [0, 0, 0];
  const coordinates = [0, 0, 0];
  for (let index = 0; index < output.length; index += 4) {
    const red = output[index];
    const green = output[index + 1];
    const blue = output[index + 2];
    coordinates[0] = red / 255;
    coordinates[1] = green / 255;
    coordinates[2] = blue / 255;
    sampleLutInto(lut, coordinates, sampled);

    output[index] = red + (clamp(sampled[0], 0, 1) * 255 - red) * mix;
    output[index + 1] = green + (clamp(sampled[1], 0, 1) * 255 - green) * mix;
    output[index + 2] = blue + (clamp(sampled[2], 0, 1) * 255 - blue) * mix;
  }

  return output;
}
